import { CalendarDays, MapPin, CheckCircle2, BadgeCheck, Sparkles, Navigation } from 'lucide-react';
import { RegisterButton } from './FairShell';
import { SafeHtml } from './SafeHtml';
import { formatFairDate, venueLine } from './fairApi';

const Wrap = ({ children, className = '', id, testId }) => (
  <section id={id} data-testid={testId} className={`relative py-16 sm:py-24 ${className}`}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
  </section>
);

const Heading = ({ title, subtitle, light }) => (
  <div className="mb-10 max-w-3xl">
    {subtitle && <p className={`text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3 ${light ? 'text-amber-300' : 'text-[var(--jf-primary)]'}`}>{subtitle}</p>}
    <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight ${light ? 'text-white' : 'text-[var(--jf-navy)]'}`}>{title}</h2>
  </div>
);

const Buttons = ({ fair, buttons, light, sid }) => (
  buttons?.length > 0 && (
    <div className="flex flex-wrap gap-3 mt-8">
      {buttons.map((b, i) => (b.action === 'url'
        ? <a key={i} href={b.url} target="_blank" rel="noreferrer" data-testid={`section-${sid}-link-${i}`}
            className={`inline-flex items-center rounded-full px-6 py-3 font-semibold border-2 transition-colors ${light ? 'border-white/40 text-white hover:bg-white/10' : 'border-[var(--jf-navy)] text-[var(--jf-navy)] hover:bg-[var(--jf-navy)] hover:text-white'}`}>{b.label}</a>
        : <RegisterButton key={i} fair={fair} kind={b.action} label={b.label} size="lg"
            variant={light ? (i === 0 ? 'light' : 'dark') : (i === 0 ? 'primary' : 'dark')} testId={`section-${sid}-btn-${i}`} />))}
    </div>
  )
);

export const FairHero = ({ fair }) => (
  <section data-testid="jobfair-hero" className="relative overflow-hidden jf-grain bg-gradient-to-b from-[#FFF4EC] via-[#FFFBF7] to-[#FFFBF7]">
    <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-[var(--jf-primary)]/15 blur-3xl" />
    <div className="absolute top-40 -left-40 w-96 h-96 rounded-full bg-amber-300/20 blur-3xl" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-16 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
      <div className="lg:col-span-7">
        {(formatFairDate(fair) || venueLine(fair)) && (
          <div data-testid="jobfair-hero-date" className="jf-rise inline-flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 rounded-full bg-white border border-orange-200 text-sm font-semibold text-[var(--jf-navy)] shadow-sm mb-6">
            {formatFairDate(fair) && <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-[var(--jf-primary)]" />{formatFairDate(fair)}</span>}
            {venueLine(fair) && <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[var(--jf-primary)]" />{venueLine(fair)}</span>}
          </div>
        )}
        <h1 data-testid="jobfair-hero-title" className="jf-rise jf-d1 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[var(--jf-navy)]">{fair.name}</h1>
        {fair.slogan && <p className="jf-rise jf-d2 mt-5 text-xl sm:text-2xl font-bold text-[var(--jf-primary)]">{fair.slogan}</p>}
        {fair.tagline && <p className="jf-rise jf-d2 mt-3 text-base sm:text-lg text-slate-600 max-w-2xl">{fair.tagline}</p>}
        {fair.short_description && <p className="jf-rise jf-d3 mt-3 text-sm sm:text-base text-slate-500 max-w-2xl">{fair.short_description}</p>}
        <div className="jf-rise jf-d4 flex flex-wrap gap-3 mt-8">
          <RegisterButton fair={fair} kind="candidate" label="Register as Job Seeker" size="lg" testId="jobfair-hero-seeker-cta" />
          <RegisterButton fair={fair} kind="employer" label="Register as Employer" size="lg" variant="dark" testId="jobfair-hero-employer-cta" />
        </div>
        {fair.effective_status === 'active' && <p className="mt-4 text-sm text-emerald-700 font-semibold inline-flex items-center gap-1.5"><BadgeCheck className="w-4 h-4" /> Registration is FREE</p>}
      </div>
      <div className="lg:col-span-5 relative">
        {fair.hero_image ? (
          <div className="relative jf-rise jf-d2">
            <div className="absolute -inset-3 rounded-[2.5rem] bg-gradient-to-br from-[var(--jf-primary)] to-amber-400 rotate-3 opacity-90" />
            <img src={fair.hero_image} alt={fair.name} className="relative w-full aspect-[4/5] object-cover rounded-[2rem] shadow-2xl" />
            <div className="jf-float absolute -bottom-6 -left-4 sm:-left-8 bg-white rounded-2xl shadow-xl px-5 py-4 flex items-center gap-3">
              <Sparkles className="w-8 h-8 text-[var(--jf-primary)]" />
              <div><p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Status</p>
                <p data-testid="jobfair-hero-status" className="font-extrabold text-[var(--jf-navy)]">{{ completed: 'Completed', draft: 'Draft Preview' }[fair.effective_status] || 'Registrations Open'}</p></div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
    {fair.highlights?.length > 0 && (
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {fair.highlights.map((h, i) => (
            <div key={i} data-testid={`jobfair-highlight-${i}`} className={`jf-rise jf-d${Math.min(i + 1, 4)} rounded-2xl p-6 bg-white/80 backdrop-blur-md border border-orange-100 shadow-xl shadow-orange-900/5 transition-transform duration-300 hover:-translate-y-1`}>
              <p className="jf-heading text-3xl sm:text-4xl font-black text-[var(--jf-primary)]">{h.value}</p>
              <p className="mt-1 text-sm font-semibold text-slate-600 uppercase tracking-wide">{h.label}</p>
            </div>
          ))}
        </div>
      </div>
    )}
  </section>
);

const TextSection = ({ s, fair, i }) => (
  <Wrap className={i % 2 ? 'bg-white' : ''} testId={`jobfair-section-${i}`}>
    <div className={`grid gap-10 lg:gap-16 items-center ${s.image ? 'lg:grid-cols-2' : ''}`}>
      <div className={s.image && s.image_position === 'left' ? 'lg:order-2' : ''}>
        <Heading title={s.title} subtitle={s.subtitle} />
        <SafeHtml html={s.content_html} className="text-base sm:text-lg text-slate-600" />
        <Buttons fair={fair} buttons={s.buttons} sid={i} />
      </div>
      {s.image && <img src={s.image} alt={s.title} className="w-full aspect-[4/3] object-cover rounded-[2rem] shadow-xl" />}
    </div>
  </Wrap>
);

const ListSection = ({ s, fair, i, check }) => (
  <Wrap className={i % 2 ? 'bg-white' : ''} testId={`jobfair-section-${i}`}>
    <div className="grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <Heading title={s.title} subtitle={s.subtitle} />
        <SafeHtml html={s.content_html} className="text-slate-600" />
        {s.image && <img src={s.image} alt="" className="mt-6 w-full aspect-video object-cover rounded-3xl" />}
        <Buttons fair={fair} buttons={s.buttons} sid={i} />
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-3 content-start">
        {(s.items || []).map((it, k) => (
          <div key={k} className="flex items-start gap-3 p-4 rounded-2xl bg-white border border-slate-200/80 shadow-sm transition-[border-color,transform] duration-200 hover:border-[var(--jf-primary)]/50 hover:-translate-y-0.5">
            {check ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <span className="w-2.5 h-2.5 rounded-full bg-[var(--jf-primary)] mt-2 shrink-0" />}
            <div><p className="font-semibold text-slate-800">{it.title}</p>{it.text && <p className="text-sm text-slate-500 mt-0.5">{it.text}</p>}</div>
          </div>
        ))}
      </div>
    </div>
  </Wrap>
);

const ChipsSection = ({ s, fair, i }) => (
  <Wrap className="bg-[var(--jf-navy)] overflow-hidden" testId={`jobfair-section-${i}`}>
    <Heading title={s.title} subtitle={s.subtitle} light />
    <SafeHtml html={s.content_html} className="text-slate-300 mb-6" />
    <div className="flex flex-wrap gap-2 sm:gap-3">
      {(s.items || []).map((it, k) => (
        <span key={k} className="px-4 py-2 rounded-xl text-sm font-semibold bg-white/10 text-white border border-white/15 transition-colors duration-200 hover:bg-[var(--jf-primary)] cursor-default">{it.title}</span>
      ))}
    </div>
    <Buttons fair={fair} buttons={s.buttons} light sid={i} />
  </Wrap>
);

const StepsSection = ({ s, fair, i }) => (
  <Wrap testId={`jobfair-section-${i}`}>
    <Heading title={s.title} subtitle={s.subtitle} />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {(s.items || []).map((it, k) => (
        <div key={k} className="group relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm transition-[box-shadow,transform] duration-300 hover:shadow-xl hover:-translate-y-1">
          <span className="w-11 h-11 rounded-xl bg-orange-100 text-[var(--jf-primary)] font-black text-lg flex items-center justify-center mb-4 transition-colors group-hover:bg-[var(--jf-primary)] group-hover:text-white">{k + 1}</span>
          <p className="font-bold text-[var(--jf-navy)]">{it.title}</p>
          {it.text && <p className="text-sm text-slate-500 mt-1">{it.text}</p>}
        </div>
      ))}
    </div>
    <Buttons fair={fair} buttons={s.buttons} sid={i} />
  </Wrap>
);

const HighlightSection = ({ s, fair, i }) => (
  <Wrap testId={`jobfair-section-${i}`} className="!py-10">
    <div className="relative overflow-hidden rounded-[2rem] bg-emerald-950 text-white p-8 sm:p-12">
      <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
      <p className="relative text-xs font-bold uppercase tracking-[0.25em] text-emerald-300 mb-6">{s.title}</p>
      <div className="relative grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {(s.items || []).map((it, k) => (
          <div key={k} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
            <BadgeCheck className="w-6 h-6 text-emerald-400 shrink-0" /><p className="font-bold">{it.title}</p>
          </div>
        ))}
      </div>
      <SafeHtml html={s.content_html} className="relative mt-6 text-sm text-emerald-100/80" />
      <Buttons fair={fair} buttons={s.buttons} light sid={i} />
    </div>
  </Wrap>
);

const CtaSection = ({ s, fair, i }) => (
  <Wrap testId={`jobfair-section-${i}`}>
    <div className="relative overflow-hidden rounded-[2.5rem] bg-[var(--jf-primary)] px-6 py-14 sm:px-14 text-white">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
      {s.image && <img src={s.image} alt="" className="absolute right-0 top-0 h-full w-1/3 object-cover opacity-30 hidden lg:block" />}
      <div className="relative max-w-3xl">
        <h2 className="text-3xl sm:text-5xl font-black tracking-tight">{s.title}</h2>
        {s.subtitle && <p className="mt-4 text-lg sm:text-xl font-semibold text-white/90">{s.subtitle}</p>}
        <SafeHtml html={s.content_html} className="mt-4 text-white/85" />
        {(formatFairDate(fair) || venueLine(fair)) && <p className="mt-4 text-sm font-semibold text-white/90">{[formatFairDate(fair), venueLine(fair)].filter(Boolean).join(' · ')}</p>}
        <Buttons fair={fair} buttons={s.buttons} light sid={i} />
      </div>
    </div>
  </Wrap>
);

const RENDERERS = {
  text: TextSection, list: ListSection, chips: ChipsSection, steps: StepsSection,
  highlight: HighlightSection, cta: CtaSection,
  checklist: (p) => <ListSection {...p} check />,
};

export const FairSections = ({ fair }) => (fair.sections || []).filter((s) => s.visible !== false).map((s, i) => {
  const R = RENDERERS[s.type] || TextSection;
  return <R key={s.id || i} s={s} fair={fair} i={i} />;
});

export const FairDetail = ({ fair }) => (fair.detail_description ? (
  <Wrap className="bg-white" testId="jobfair-detail">
    <SafeHtml html={fair.detail_description} className="max-w-4xl text-base sm:text-lg text-slate-600" />
  </Wrap>
) : null);

export const FairVenue = ({ fair }) => {
  if (!fair.venue_name && !fair.address) return null;
  const hasLL = fair.latitude && fair.longitude;
  const q = hasLL ? `${fair.latitude},${fair.longitude}` : [fair.venue_name, fair.address, fair.city].filter(Boolean).join(', ');
  const mapLink = fair.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  return (
    <Wrap testId="jobfair-venue" className="bg-white">
      <div className="grid lg:grid-cols-5 gap-8 items-stretch">
        <div className="lg:col-span-2 flex flex-col justify-center">
          <Heading title="Venue" subtitle="Where to come" />
          <p className="text-xl font-bold text-[var(--jf-navy)]">{fair.venue_name}</p>
          {fair.address && <p className="mt-2 text-slate-600 whitespace-pre-line">{fair.address}</p>}
          {formatFairDate(fair) && <p className="mt-4 inline-flex items-center gap-2 font-semibold text-slate-700"><CalendarDays className="w-5 h-5 text-[var(--jf-primary)]" />{formatFairDate(fair)}</p>}
          <a href={mapLink} target="_blank" rel="noreferrer" data-testid="jobfair-venue-directions"
            className="mt-6 self-start inline-flex items-center gap-2 rounded-full bg-[var(--jf-navy)] text-white px-5 py-2.5 font-semibold hover:bg-slate-800 transition-colors">
            <Navigation className="w-4 h-4" /> Get Directions
          </a>
        </div>
        <div className="lg:col-span-3 rounded-[2rem] overflow-hidden border border-slate-200 min-h-[320px]">
          <iframe title="Venue map" className="w-full h-full min-h-[320px]" loading="lazy"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=15&output=embed`} />
        </div>
      </div>
    </Wrap>
  );
};
